import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Building2, PowerOff, Eye } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import SearchInput from "@/components/SearchInput";
import { useWithBack } from "@/lib/nav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function CustomersList() {
  const withBackHref = useWithBack();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("customer.write");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  // Monotonic request id — the newest fetch wins even if an older one is still in flight.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (search) params.search = search;
      if (showInactive) params.active = "all";
      const { data } = await api.get("/customers", { params });
      if (myReqId !== reqIdRef.current) return; // stale response, drop it
      setItems(data.items);
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [search, showInactive]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const handleDeactivate = async (row) => {
    try {
      await api.delete(`/customers/${row.id}`);
      toast.success(`${row.name} deactivated`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="customers-page">
      <PageHeader
        testId="customers-header"
        title="Customers"
        subtitle="Companies you supply and service machines for."
        actions={
          canWrite && (
            <Button asChild size="sm" data-testid="customer-new-button">
              <Link to="/customers/new"><Plus className="h-4 w-4" /> New Customer</Link>
            </Button>
          )
        }
      />

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search customers…"
          testId="customers-search"
        />
        <div className="flex items-center gap-2">
          <Label htmlFor="show-inactive" className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Show inactive</Label>
          <Switch id="show-inactive" checked={showInactive} onCheckedChange={setShowInactive} data-testid="customers-show-inactive" />
        </div>
      </div>

      <Card className="rounded-md overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Primary Contact</th>
                <th className="text-left py-2 px-3">Email</th>
                <th className="text-left py-2 px-3">Phone</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-xs text-muted-foreground" data-testid="customers-empty">
                  No customers found. {canWrite && (<Link to="/customers/new" className="text-primary underline">Create one</Link>)}.
                </td></tr>
              ) : items.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="py-2 px-3">
                    <Link to={withBackHref(`/customers/${c.id}`)} className="font-medium hover:text-primary flex items-center gap-2" data-testid={`customer-link-${c.id}`}>
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {c.name}
                    </Link>
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">{(c.contacts?.[0]?.name) || "—"}</td>
                  <td className="py-2 px-3 text-muted-foreground">{(c.contacts?.[0]?.email) || "—"}</td>
                  <td className="py-2 px-3 text-muted-foreground">{(c.contacts?.[0]?.phone) || "—"}</td>
                  <td className="py-2 px-3">
                    {c.active
                      ? <StatusBadge tone="success" testId={`customer-status-${c.id}`}>Active</StatusBadge>
                      : <StatusBadge tone="muted" testId={`customer-status-${c.id}`}>Inactive</StatusBadge>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`customer-actions-${c.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/customers/${c.id}`)} data-testid={`customer-view-${c.id}`}>
                          <Eye className="h-4 w-4 mr-2" /> View
                        </DropdownMenuItem>
                        {canWrite && <DropdownMenuItem onClick={() => navigate(`/customers/${c.id}/edit`)} data-testid={`customer-edit-${c.id}`}>
                          <Pencil className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>}
                        {canWrite && c.active && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDeactivate(c)} data-testid={`customer-deactivate-${c.id}`} className="text-destructive focus:text-destructive">
                              <PowerOff className="h-4 w-4 mr-2" /> Deactivate
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
